import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AccountLayout } from 'src/sections/account/account-layout';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';
import {
  LegacyContentRedirect,
  LegacyWorksheetRedirect,
  DashboardWorkspaceRedirect,
  LegacyContentEditorRedirect,
  LegacyWorksheetTestRedirect,
} from '../components/workspace-redirect';

// ----------------------------------------------------------------------

// Overview
const OverviewPage = lazy(() => import('src/pages/dashboard/overview'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
const OverviewCoursePage = lazy(() => import('src/pages/dashboard/course'));
// Product
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// Order
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// Invoice
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// User
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// Account
const AccountGeneralPage = lazy(() => import('src/pages/dashboard/user/account/general'));
const AccountChangePasswordPage = lazy(
  () => import('src/pages/dashboard/user/account/change-password')
);
const AccountSubscriptionPage = lazy(() => import('src/pages/dashboard/user/account/subscription'));
// Blog
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// Job
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// Tour
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// File manager
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// App
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// Test render page by role
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// Blank page
const ParamsPage = lazy(() => import('src/pages/dashboard/params'));
const SubpathsPage = lazy(() => import('src/pages/dashboard/subpaths'));
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
// Seet
const SeetPage = lazy(() => import('src/pages/seet'));
const DashboardSeetPage = lazy(() => import('src/pages/seet/dashboard-seet'));
const ContentPage = lazy(() => import('src/pages/dashboard/content'));
const ContentEditorPage = lazy(() => import('src/pages/dashboard/content/editor'));
const H5PContentEditorPage = lazy(() => import('src/pages/dashboard/content/edit'));
const WorksheetEditorPage = lazy(() => import('src/pages/dashboard/worksheet/worksheetId'));
const WorksheetTestPage = lazy(() => import('src/pages/dashboard/worksheet/test'));
// Groups
const GroupsPage = lazy(() => import('src/pages/dashboard/groups'));
const GroupLayoutWrapperPage = lazy(
  () => import('src/pages/dashboard/groups/group-layout-wrapper')
);
const SeatingViewPage = lazy(() => import('src/pages/dashboard/groups/seating-view'));
const AnalyticsViewPage = lazy(() => import('src/pages/dashboard/groups/analytics-view'));
const GroupSettingsPage = lazy(() => import('src/pages/dashboard/groups/settings'));
const AssignmentViewerPage = lazy(() => import('src/pages/dashboard/groups/assignment-viewer'));
// Learners
const LearnersPage = lazy(() => import('src/pages/dashboard/learners'));
const LearnerDetailPage = lazy(() => import('src/pages/dashboard/learners/detail'));
// Deployments
const DeploymentsPage = lazy(() => import('src/pages/dashboard/deployments'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const accountLayout = () => (
  <AccountLayout>
    <SuspenseOutlet />
  </AccountLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { index: true, element: <DashboardWorkspaceRedirect /> },
      { path: 'overview', element: <OverviewPage /> },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      { path: 'course', element: <OverviewCoursePage /> },
      {
        path: 'user',
        children: [
          { index: true, element: <UserProfilePage /> },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          {
            path: 'account',
            element: accountLayout(),
            children: [
              { index: true, element: <AccountGeneralPage /> },
              {
                path: 'security',
                element: <AccountChangePasswordPage />,
              },
              {
                path: 'subscription',
                element: <AccountSubscriptionPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'product',
        children: [
          { index: true, element: <ProductListPage /> },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { index: true, element: <OrderListPage /> },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { index: true, element: <InvoiceListPage /> },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { index: true, element: <BlogPostsPage /> },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { index: true, element: <JobListPage /> },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { index: true, element: <TourListPage /> },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'params', element: <ParamsPage /> },
      { path: 'blank', element: <BlankPage /> },
      { path: 'seet', element: <SeetPage /> },
      { path: 'seet/:seetId', element: <DashboardSeetPage /> },
      {
        path: 'subpaths',
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/subpaths/sub-1/sub-2" />,
          },
          { path: 'sub-1/sub-2', element: <SubpathsPage /> },
        ],
      },
      // New workspace-based routes
      {
        path: 'workspace/:workspaceId/folder/:folderId/content',
        element: <ContentPage />,
      },
      {
        path: 'workspace/:workspaceId/folder/:folderId/content/:contentId',
        element: <ContentEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/folder/:folderId/content/:contentId/edit',
        element: <H5PContentEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/worksheet/:worksheetId/edit',
        element: <WorksheetEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/worksheet/test',
        element: <WorksheetTestPage />,
      },
      // Legacy redirects
      { path: 'content', element: <LegacyContentRedirect /> },
      { path: 'content/:contentId', element: <LegacyContentEditorRedirect /> },
      {
        path: 'content/:contentId/edit',
        element: <LegacyContentEditorRedirect />,
      },
      {
        path: 'worksheet/:worksheetId/edit',
        element: <LegacyWorksheetRedirect />,
      },
      { path: 'worksheet/test', element: <LegacyWorksheetTestRedirect /> },
      { path: 'groups', element: <GroupsPage /> },
      {
        path: 'groups/:groupId',
        element: <GroupLayoutWrapperPage />,
        children: [
          { index: true, element: <Navigate to="seating" replace /> },
          { path: 'seating', element: <SeatingViewPage /> },
          { path: 'analytics', element: <AnalyticsViewPage /> },
          { path: 'settings', element: <GroupSettingsPage /> },
        ],
      },
      {
        path: 'groups/:groupId/assignments/:assignmentId',
        element: <AssignmentViewerPage />,
      },
      { path: 'learners', element: <LearnersPage /> },
      { path: 'learners/:learnerId', element: <LearnerDetailPage /> },
      { path: 'deployments', element: <DeploymentsPage /> },
      { path: 'deployments/:deploymentId', element: <DeploymentsPage /> },
      { path: 'deployments/:deploymentId/:tab', element: <DeploymentsPage /> },
    ],
  },
];
